/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { ResponseError } from "client";
import { Const } from "const";
import { HttpError } from "exception/HttpError";
import { t } from "i18next";

/**
 * Save access token.
 */
export const globalVariable = {
  accessToken: "",
};

/**
 * API error response
 */
export interface ErrorResponse {
  /** Status code */
  statusCode: number;
  /** Error codes */
  errors?: string[];
}

/**
 * Get error response from Error object
 * @param {unknown} error Error object
 * @return {ErrorResponse} Error response
 */
export async function getErrorResponse(
  error: unknown
): Promise<ErrorResponse | undefined> {
  if (error instanceof HttpError) {
    return {
      statusCode: error.statusCode,
      errors: error.errorCodes,
    };
  } else if (error instanceof ResponseError) {
    try {
      const responseBody = await error.response.json();
      return {
        statusCode: error.response.status,
        errors: responseBody.errors,
      };
    } catch (err) {
      return {
        statusCode: error.response.status,
      };
    }
  }
}

/**
 * task create progress error handling
 * @param {unknown} error
 * @return {string} message code
 */
export async function getExtendErrMsgCode(error: unknown) {
  const errorResponse = await getErrorResponse(error);
  if (errorResponse?.errors?.includes("UI-100150003")) {
    return "AWVUI-MSG90";
  } else if (errorResponse?.errors?.includes("UI-100150004")) {
    return "AWVUI-MSG91";
  } else if (errorResponse?.errors?.includes("UI-100150005")) {
    return "AWVUI-MSG178";
  } else if (errorResponse?.errors?.includes("UI-100150006")) {
    return "AWVUI-MSG179";
  } else if (errorResponse?.errors?.includes("UI-100150007")) {
    return "AWVUI-MSG180";
  } else if (errorResponse?.errors?.includes("202008004")) {
    return "AWVUI-MSG170";
  } else if (errorResponse?.errors?.includes("202008010")) {
    return "AWVUI-MSG170";
  } else if (errorResponse?.errors?.includes("202003002")) {
    return "AWVUI-MSG171";
  } else if (errorResponse?.errors?.includes("202008009")) {
    return "AWVUI-MSG171";
  }
  return "";
}

/**
 * task create progress error handling to display error code
 * @param {unknown} error
 * @return {string} error code message
 */
export async function getErrCodeMsg(error: unknown) {
  const errorResponse = await getErrorResponse(error);
  if (
    errorResponse?.errors?.includes("UI-100150005") ||
    errorResponse?.errors?.includes("UI-100150006") ||
    errorResponse?.errors?.includes("UI-100150007")
  ) {
    return `${t("AWVUI-MSG181")} ${errorResponse?.errors}${t(
      "AWVUI-MSG182"
    )}\n`;
  }
  return "";
}

/**
 * Determine whether it is necessary to add an additional message
 * @param {string} errorCode
 * @return {boolean} shouldAddErrMsg
 */
export function shouldAddErrMsg(errorCode: string): boolean {
  if (errorCode?.includes(Const.errorCodes.taskStatusIsCompleated)) {
    return true;
  } else {
    return false;
  }
}

/**
 * task cancel error handling if target task done
 * @param {number | undefined} taskStatus
 * @return {string} message code
 */
export function getExtendCancelErrMsgCode(taskStatus: number | undefined) {
  switch (taskStatus) {
    case 3:
      return "AWVUI-MSG172";
    case 5:
      return "AWVUI-MSG173";
    case 7:
      return "AWVUI-MSG174";
    default:
      return "";
  }
}
